import fileTexMultiple from '@/shared/assets/icons/FileTextMultiple.svg';
import tableIcon from '@/shared/assets/icons/TableIcon.svg';
import {
  ORDER_OPTIONS,
  QUANTITY_OPTIONS,
  SHOW_ALL_ITEMS_QUANTITY,
  type QuantityOption,
} from '@/shared/config';
import { SEARCH_TYPE_FIELD_OPTIONS, SearchTypeEnum } from '@/shared/lib';
import type { EntityType, OrderOptions, SortOrder } from '@/shared/lib';

export type VerificationDocType = 'DOCUMENT' | 'REGISTRATION';

export interface VerificationSearchField {
  id: string;
  name: string;
}

export interface VerificationTabConfig {
  tabLabel: string;
  codeColumnLabel: string;
  /**
   * Cada aba tem a sua fonte: documento le revisao de documento, registro le a matriz aprovada.
   * O endereco vive aqui, e nao no componente, para que a troca de aba troque a fonte.
   */
  searchUrl: string;
  /**
   * O menu de tipo de busca acompanha o rotulo da coluna do codigo: "Prefixo" em Documentos e
   * "Registro" em Registros. As duas ordens sao diferentes de proposito.
   */
  searchFields: VerificationSearchField[];
  quantityOptions: QuantityOption[];
  /**
   * Cada aba so oferece o que a sua fonte aceita ordenar. A fila de matriz recusa `revision`, e
   * oferecer a opcao devolvia 400 e uma torrada de erro na tela.
   */
  orderOptions: OrderOptions[];
  paginationEntity: EntityType;
  emptyTitle: string;
}

const REVISION_COUNT_SORT = 'revision';

const orderSearchFieldsBy = (ids: SearchTypeEnum[]): VerificationSearchField[] =>
  ids.flatMap((id) => SEARCH_TYPE_FIELD_OPTIONS.filter((field) => field.id === id));

const DOCUMENT_SEARCH_FIELDS: VerificationSearchField[] = orderSearchFieldsBy([
  SearchTypeEnum.DOCUMENT_NAME,
  SearchTypeEnum.PREFIX,
]);

const REGISTRATION_SEARCH_FIELDS: VerificationSearchField[] = orderSearchFieldsBy([
  SearchTypeEnum.PREFIX,
  SearchTypeEnum.DOCUMENT_NAME,
]).map((field) => (field.id === SearchTypeEnum.PREFIX ? { ...field, name: 'Registro' } : field));

const REGISTRATION_ORDER_OPTIONS: OrderOptions[] = ORDER_OPTIONS.filter(
  (option) => option.sort !== REVISION_COUNT_SORT
);

const buildQuantityOptions = (showAllLabel: string): QuantityOption[] =>
  QUANTITY_OPTIONS.map((option) =>
    option.id === SHOW_ALL_ITEMS_QUANTITY ? { ...option, name: showAllLabel } : option
  );

export const VERIFICATION_SEARCH_URL = '/document-revisions/awaiting-verification/search';
export const VERIFICATION_REGISTRATION_SEARCH_URL = '/matrices/awaiting-verification';

export const VERIFICATION_TAB_CONFIG: Record<VerificationDocType, VerificationTabConfig> = {
  DOCUMENT: {
    tabLabel: 'Documentos',
    codeColumnLabel: 'Prefixo',
    searchUrl: VERIFICATION_SEARCH_URL,
    searchFields: DOCUMENT_SEARCH_FIELDS,
    quantityOptions: buildQuantityOptions('Todos os documentos'),
    orderOptions: ORDER_OPTIONS,
    paginationEntity: 'documento',
    emptyTitle: 'Nenhum documento pendente no momento',
  },
  REGISTRATION: {
    tabLabel: 'Registros',
    codeColumnLabel: 'Registro',
    searchUrl: VERIFICATION_REGISTRATION_SEARCH_URL,
    searchFields: REGISTRATION_SEARCH_FIELDS,
    quantityOptions: buildQuantityOptions('Todos os registros'),
    orderOptions: REGISTRATION_ORDER_OPTIONS,
    paginationEntity: 'registro',
    emptyTitle: 'Nenhum registro pendente no momento',
  },
};

export const isVerificationDocType = (value: unknown): value is VerificationDocType =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(VERIFICATION_TAB_CONFIG, value);

export const getVerificationTabConfig = (documentType?: string): VerificationTabConfig =>
  isVerificationDocType(documentType)
    ? VERIFICATION_TAB_CONFIG[documentType]
    : VERIFICATION_TAB_CONFIG.DOCUMENT;

export const VERIFICATION_TAB_OPTIONS = [
  {
    id: 'DOCUMENT' as const,
    text: VERIFICATION_TAB_CONFIG.DOCUMENT.tabLabel,
    icon: fileTexMultiple,
  },
  {
    id: 'REGISTRATION' as const,
    text: VERIFICATION_TAB_CONFIG.REGISTRATION.tabLabel,
    icon: tableIcon,
  },
];

export const VERIFICATION_DEFAULT_ITEMS_PER_PAGE = 10;

export const VERIFICATION_DEFAULT_SORT = {
  sort: '',
  order: 'desc' as SortOrder,
};
